import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useContext,
  useRef,
  createContext,
  type ComponentType,
  type SVGProps,
} from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'
import { useSearchParams } from 'react-router-dom'
import {
  AdjustmentsHorizontalIcon,
  BanknotesIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  FolderIcon,
  GiftIcon,
  IdentificationIcon,
  QueueListIcon,
  RectangleStackIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { scheduleScrollAfterLayoutSettle } from '../../../../util/scroll'
import RightTooltip from '../../../../components/RightTooltip'

export interface TabPanelProps {
  selectedKey?: number
  /** Called when the user selects a tab (not when selectedKey is applied from props). */
  onSelect?: (index: number) => void
  /** When true (default), initialize from and write `?tab=`. Disable when a parent owns tab state (e.g. per-account or synced). */
  updateUrl?: boolean
  /** When true (default), scroll the enclosing tenant account into view on tab change. */
  autoScroll?: boolean
  /** Sticky chrome height to offset when auto-scrolling (page header + accounts toolbar). */
  scrollOffset?: number
  children: React.ReactNode
}

export type TabIcon = ComponentType<SVGProps<SVGSVGElement>>

type TabChildProps = {
  title: string
  /** Count or capped label (e.g. `5+`) shown beside the tab name. */
  badge?: number | string
  icon?: TabIcon
  /** Shown under the title in the content header. */
  description?: string
}

const TabHeaderActionsContext = createContext<((node: React.ReactNode) => void) | null>(null)

/** Hosts in TenantAccountPanel so the tab rail can stick with the account header while tab content stays clipped. */
export type AccountTabNavSlots = {
  topHost: HTMLElement | null
  sideHost: HTMLElement | null
}

export const AccountTabNavSlotsContext = createContext<AccountTabNavSlots | null>(null)

/** Register actions shown on the right of the selected tab content header. */
export function useTabHeaderActions(actions: React.ReactNode, enabled = true): void {
  const setHeaderActions = useContext(TabHeaderActionsContext)

  useLayoutEffect(() => {
    if (setHeaderActions == null) return
    setHeaderActions(enabled ? actions : null)
    return () => setHeaderActions(null)
  }, [setHeaderActions, enabled, actions])
}

const hasTabBadge = (badge: number | string | undefined): badge is number | string => {
  if (badge == null) return false
  if (typeof badge === 'number') return badge > 0
  return badge !== ''
}

const TAB_QUERY_PARAM = 'tab'
const TAB_NAV_COLLAPSED_KEY = 'tenantTabNavCollapsed'
/** Same-tab broadcast so every account panel's TabPanel mirrors collapse (independent of sync-tabs). */
const TAB_NAV_COLLAPSED_EVENT = 'tenantTabNavCollapsedChange'
const TAB_NAV_LAYOUT_KEY = 'tenantTabNavLayout'
const TAB_NAV_LAYOUT_EVENT = 'tenantTabNavLayoutChange'

type TabNavLayout = 'side' | 'top'

const readTabNavCollapsed = (): boolean => {
  try {
    return sessionStorage.getItem(TAB_NAV_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

const writeTabNavCollapsed = (collapsed: boolean): void => {
  try {
    sessionStorage.setItem(TAB_NAV_COLLAPSED_KEY, String(collapsed))
  } catch {
    // ignore quota / private mode
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TAB_NAV_COLLAPSED_EVENT, { detail: collapsed }))
  }
}

const readTabNavLayout = (): TabNavLayout => {
  try {
    const stored = localStorage.getItem(TAB_NAV_LAYOUT_KEY)
    if (stored === 'top' || stored === 'side') return stored
    // Promote a same-tab value written before this moved to localStorage.
    const legacy = sessionStorage.getItem(TAB_NAV_LAYOUT_KEY)
    if (legacy === 'top' || legacy === 'side') {
      try {
        localStorage.setItem(TAB_NAV_LAYOUT_KEY, legacy)
      } catch {
        // keep the known preference even if promote fails
      }
      return legacy
    }
  } catch {
    // ignore quota / private mode
  }
  return 'side'
}

const writeTabNavLayout = (layout: TabNavLayout): void => {
  try {
    localStorage.setItem(TAB_NAV_LAYOUT_KEY, layout)
  } catch {
    // ignore quota / private mode
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TAB_NAV_LAYOUT_EVENT, { detail: layout }))
  }
}

const DEFAULT_TAB_ICONS: Record<string, TabIcon> = {
  General: IdentificationIcon,
  'Mailing preference': EnvelopeIcon,
  'Service agreements': ClipboardDocumentListIcon,
  'Account managers': UsersIcon,
  Payments: CreditCardIcon,
  Invoices: BanknotesIcon,
  'Billing events': QueueListIcon,
  'Billing options': Cog6ToothIcon,
  Conductor: RectangleStackIcon,
  Incentives: GiftIcon,
  'Other Documents': FolderIcon,
}

const resolveTabIcon = (title: string, icon?: TabIcon): TabIcon | undefined =>
  icon ?? DEFAULT_TAB_ICONS[title]

const TAB_ROW_GAP_PX = 2

/** How many leading tabs fit on one row before a More control is required. */
export const countFittingTabItems = (
  tabWidths: number[],
  containerWidth: number,
  leadingWidth: number,
  moreWidth: number,
  gap = TAB_ROW_GAP_PX
): number => {
  if (tabWidths.length === 0) return 0
  if (containerWidth <= 0) return tabWidths.length

  // moreWidth 0: overflow is hosted outside this row. Do not reserve a More control or its leading gap.
  const reserveMore = moreWidth > 0
  const widthFor = (count: number, includeMore: boolean): number => {
    let total = leadingWidth
    for (let i = 0; i < count; i++) {
      total += gap + tabWidths[i]!
    }
    if (includeMore && reserveMore) total += gap + moreWidth
    return total
  }

  if (widthFor(tabWidths.length, false) <= containerWidth) return tabWidths.length

  let count = tabWidths.length - 1
  while (count > 0 && widthFor(count, reserveMore) > containerWidth) {
    count -= 1
  }
  return count
}

interface TabNavSettingsProps {
  navCollapsed: boolean
  isTopLayout: boolean
  onToggleCollapsed: () => void
  onToggleLayout: () => void
}

/** Compact layout menu — same chrome as HeaderChipsPicker. Portaled so the rail cannot clip it. */
const TabNavSettings: React.FC<TabNavSettingsProps> = ({
  navCollapsed,
  isTopLayout,
  onToggleCollapsed,
  onToggleLayout,
}) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const close = useCallback(() => setOpen(false), [])

  const placeMenu = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect == null) return
    setPos(
      isTopLayout
        ? { top: rect.bottom + 6, left: rect.left }
        : { top: rect.top, left: rect.right + 6 }
    )
  }, [isTopLayout])

  useEffect(() => {
    if (!open) return
    placeMenu()
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) === true) return
      if (menuRef.current?.contains(target) === true) return
      close()
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', placeMenu)
    window.addEventListener('scroll', placeMenu, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', placeMenu)
      window.removeEventListener('scroll', placeMenu, true)
    }
  }, [open, close, placeMenu])

  const itemClass =
    'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/60'

  return (
    <div className="relative shrink-0 self-center">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Customize tab bar"
        title="Customize tab bar"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-slate-500 hover:border-slate-300 hover:bg-white/70 hover:text-slate-800 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:bg-slate-800/70 dark:hover:text-slate-200"
      >
        <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Tab bar"
              className="fixed z-[200] w-48 rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
              style={{ top: pos.top, left: pos.left }}
            >
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  onToggleCollapsed()
                  close()
                }}
              >
                <ChevronLeftIcon
                  className={classnames(
                    'h-4 w-4 shrink-0',
                    isTopLayout ? (navCollapsed ? '-rotate-90' : 'rotate-90') : navCollapsed && 'rotate-180'
                  )}
                  aria-hidden
                />
                {navCollapsed ? 'Expand tabs' : 'Collapse tabs'}
              </button>
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  onToggleLayout()
                  close()
                }}
              >
                {isTopLayout ? (
                  <>
                    <Bars3BottomLeftIcon className="h-4 w-4 shrink-0" aria-hidden />
                    Move tabs to side
                  </>
                ) : (
                  <>
                    <Bars3Icon className="h-4 w-4 shrink-0" aria-hidden />
                    Pin tabs to top
                  </>
                )}
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

interface OverflowTabItem {
  index: number
  name: string
  badge?: number | string
  Icon?: TabIcon
  selected: boolean
}

interface TabMoreMenuProps {
  items: OverflowTabItem[]
  showLabels: boolean
  hasSelected: boolean
  onSelect: (index: number) => void
}

const TabMoreMenu: React.FC<TabMoreMenuProps> = ({ items, showLabels, hasSelected, onSelect }) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, right: 0 })
  const close = useCallback(() => setOpen(false), [])

  const placeMenu = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect == null) return
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
  }, [])

  useEffect(() => {
    if (!open) return
    placeMenu()
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) === true) return
      if (menuRef.current?.contains(target) === true) return
      close()
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', placeMenu)
    window.addEventListener('scroll', placeMenu, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', placeMenu)
      window.removeEventListener('scroll', placeMenu, true)
    }
  }, [open, close, placeMenu])

  return (
    <div className="relative shrink-0 self-center">
      <RightTooltip content="More tabs" enabled={!showLabels} placement="top" triggerClassName="w-fit">
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-haspopup="true"
          aria-label="More tabs"
          aria-current={hasSelected ? 'page' : undefined}
          onClick={() => setOpen((prev) => !prev)}
          className={classnames(
            'relative flex cursor-pointer items-center rounded-md border-b-2 py-2 text-sm font-medium transition-[padding,gap] duration-200 ease-in-out',
            hasSelected
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-500/15 dark:text-indigo-300'
              : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200',
            showLabels ? 'justify-start gap-2 px-3' : 'justify-center px-1.5'
          )}
        >
          <EllipsisHorizontalIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span
            className={classnames(
              'min-w-0 truncate transition-[opacity,max-width] duration-150 ease-in-out',
              showLabels ? 'max-w-[4rem] opacity-100' : 'max-w-0 opacity-0'
            )}
            aria-hidden={!showLabels}
          >
            More
          </span>
          {hasSelected && !showLabels ? (
            <span
              className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-indigo-500"
              aria-hidden
            />
          ) : null}
        </button>
      </RightTooltip>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="More tabs"
              className="fixed z-[200] w-56 rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
              style={{ top: pos.top, right: pos.right }}
            >
              {items.map((item) => {
                const Icon = item.Icon
                return (
                  <button
                    key={item.index}
                    type="button"
                    role="menuitem"
                    aria-current={item.selected ? 'page' : undefined}
                    className={classnames(
                      'flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs font-medium',
                      item.selected
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
                        : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/60'
                    )}
                    onClick={() => {
                      onSelect(item.index)
                      close()
                    }}
                  >
                    {Icon != null ? (
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    ) : (
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[10px] font-semibold" aria-hidden>
                        {item.name.charAt(0)}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    {hasTabBadge(item.badge) ? (
                      <span className="inline-flex items-center justify-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-200">
                        {item.badge}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

const useIsXlUp = (): boolean => {
  const [isXlUp, setIsXlUp] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
    return window.matchMedia('(min-width: 1280px)').matches
  })

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(min-width: 1280px)')
    const onChange = () => setIsXlUp(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return isXlUp
}

export const TabPanel: React.FC<TabPanelProps> = ({
  selectedKey: controlledKey,
  onSelect,
  updateUrl = true,
  autoScroll = true,
  scrollOffset = 0,
  children,
}) => {
  const isXlUp = useIsXlUp()
  const [navCollapsed, setNavCollapsed] = useState(readTabNavCollapsed)
  const [navLayout, setNavLayout] = useState<TabNavLayout>(readTabNavLayout)
  const isTopLayout = navLayout === 'top'
  const navSlots = useContext(AccountTabNavSlotsContext)
  const navPortalHost = navSlots == null ? null : isTopLayout ? navSlots.topHost : navSlots.sideHost
  const navInStickyHeader = navSlots != null && isTopLayout
  // Match main sidebar: labels follow user preference; below xl we auto-collapse but still allow expand.
  const showLabels = !navCollapsed
  const [searchParams, setSearchParams] = useSearchParams()
  const tabChildren = React.Children.toArray(children).filter(React.isValidElement)
  const childCount = tabChildren.length
  const maxIndex = Math.max(0, childCount - 1)
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const tabRowRef = useRef<HTMLDivElement>(null)
  const measureRowRef = useRef<HTMLDivElement>(null)
  const moreMeasureRef = useRef<HTMLDivElement>(null)
  const cancelPendingScrollRef = useRef<(() => void) | null>(null)
  const [visibleTabCount, setVisibleTabCount] = useState(childCount)

  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed((prev) => {
      const next = !prev
      writeTabNavCollapsed(next)
      return next
    })
  }, [])

  const toggleNavLayout = useCallback(() => {
    setNavLayout((prev) => {
      const next: TabNavLayout = prev === 'top' ? 'side' : 'top'
      writeTabNavLayout(next)
      return next
    })
  }, [])

  // Keep every mounted TabPanel (one per tenant account) on the same collapse preference.
  useEffect(() => {
    const onCollapseChange = (event: Event) => {
      const detail = (event as CustomEvent<boolean>).detail
      setNavCollapsed(typeof detail === 'boolean' ? detail : readTabNavCollapsed())
    }
    window.addEventListener(TAB_NAV_COLLAPSED_EVENT, onCollapseChange)
    return () => window.removeEventListener(TAB_NAV_COLLAPSED_EVENT, onCollapseChange)
  }, [])

  useEffect(() => {
    const onLayoutChange = (event: Event) => {
      const detail = (event as CustomEvent<TabNavLayout>).detail
      setNavLayout(detail === 'top' || detail === 'side' ? detail : readTabNavLayout())
    }
    window.addEventListener(TAB_NAV_LAYOUT_EVENT, onLayoutChange)
    return () => window.removeEventListener(TAB_NAV_LAYOUT_EVENT, onLayoutChange)
  }, [])

  // Auto-collapse the tab rail when the viewport drops below xl (same idea as the app Nav).
  // Do not persist the auto-collapse — restore the user's preference when returning to xl+.
  const wasXlUpRef = useRef(isXlUp)
  useEffect(() => {
    if (!isXlUp && wasXlUpRef.current) {
      setNavCollapsed(true)
    } else if (isXlUp && !wasXlUpRef.current) {
      setNavCollapsed(readTabNavCollapsed())
    }
    wasXlUpRef.current = isXlUp
  }, [isXlUp])

  useEffect(() => {
    return () => {
      cancelPendingScrollRef.current?.()
      cancelPendingScrollRef.current = null
    }
  }, [])

  // Precedence for the initial tab:
  //   1. controlled `selectedKey` prop (used by sync tabs / per-account state in Tenant.tsx)
  //   2. `?tab=<index>` in the URL when this panel owns the URL (`updateUrl`)
  //   3. 0
  // When updateUrl is false, never seed from the shared URL — that would look like syncing.
  const clampTabIndex = (index: number): number => Math.min(Math.max(0, index), maxIndex)

  const [selectedKey, setSelectedKey] = useState(() => {
    if (controlledKey != null) return clampTabIndex(controlledKey)
    if (!updateUrl) return 0
    const raw = searchParams.get(TAB_QUERY_PARAM)
    if (raw == null) return 0
    const parsed = parseInt(raw, 10)
    if (!Number.isFinite(parsed) || parsed < 0) return 0
    return clampTabIndex(parsed)
  })

  useLayoutEffect(() => {
    if (controlledKey != null) {
      setSelectedKey(clampTabIndex(controlledKey))
    }
  }, [controlledKey, maxIndex])

  const tabs = tabChildren.map((child) => {
    const props = child.props as TabChildProps
    return {
      name: props.title,
      key: child.key,
      badge: props.badge,
      description: props.description,
      Icon: resolveTabIcon(props.title, props.icon),
    }
  })

  const measureTopTabOverflow = useCallback(() => {
    if (!isTopLayout) {
      setVisibleTabCount(tabs.length)
      return
    }
    const row = tabRowRef.current
    const measureRow = measureRowRef.current
    if (row == null || measureRow == null) return
    const containerWidth = row.clientWidth
    const settingsEl = row.firstElementChild instanceof HTMLElement ? row.firstElementChild : null
    const leadingWidth = settingsEl?.offsetWidth ?? 0
    const tabEls = [...measureRow.querySelectorAll<HTMLElement>('[data-tab-measure]')]
    const tabWidths = tabEls.map((el) => el.offsetWidth)
    const moreWidth = moreMeasureRef.current?.offsetWidth ?? 72
    setVisibleTabCount(countFittingTabItems(tabWidths, containerWidth, leadingWidth, moreWidth))
  }, [isTopLayout, tabs.length, showLabels])

  useLayoutEffect(() => {
    measureTopTabOverflow()
    const row = tabRowRef.current
    // Hosts start null; without this dep the first pass bails and never observes the portaled row.
    if (row == null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measureTopTabOverflow())
    observer.observe(row)
    return () => observer.disconnect()
  }, [measureTopTabOverflow, navPortalHost])

  const scrollAccountIntoView = useCallback(() => {
    // Wait until account-list layout settles (all expanded panels, sync-tab siblings).
    cancelPendingScrollRef.current?.()
    const section = rootRef.current?.closest('section')
    const accountsRoot =
      rootRef.current?.closest('[data-tenant-accounts]') instanceof HTMLElement
        ? (rootRef.current.closest('[data-tenant-accounts]') as HTMLElement)
        : null
    cancelPendingScrollRef.current = scheduleScrollAfterLayoutSettle(
      section,
      scrollOffset,
      accountsRoot ?? contentRef.current ?? rootRef.current,
      { settleMs: 250, maxWaitMs: 3000, correctAfterMs: 400 }
    )
  }, [scrollOffset])

  const selectTab = useCallback(
    (index: number) => {
      const changed = index !== selectedKey
      setSelectedKey(index)
      onSelect?.(index)
      if (updateUrl) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set(TAB_QUERY_PARAM, String(index))
            return next
          },
          { replace: true }
        )
      }
      if (changed && autoScroll) {
        // Wait until React commits this tab + sync-tab siblings (useLayoutEffect).
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scrollAccountIntoView()
          })
        })
      }
    },
    [setSearchParams, onSelect, updateUrl, selectedKey, scrollAccountIntoView, autoScroll]
  )

  const tooltipPlacement = isTopLayout ? 'top' : 'right'
  const overflowStart = isTopLayout ? Math.min(visibleTabCount, tabs.length) : tabs.length
  const overflowItems: OverflowTabItem[] = isTopLayout
    ? tabs.slice(overflowStart).map((tab, offset) => ({
        index: overflowStart + offset,
        name: tab.name,
        badge: tab.badge,
        Icon: tab.Icon,
        selected: selectedKey === overflowStart + offset,
      }))
    : []
  const overflowHasSelected = overflowItems.some((item) => item.selected)

  const renderTabTrigger = (tab: (typeof tabs)[number], index: number, measure = false) => {
    const Icon = tab.Icon
    const selected = selectedKey === index
    return (
      <a
        key={tab.name}
        data-tab-measure={measure ? '' : undefined}
        onClick={measure ? undefined : () => selectTab(index)}
        aria-label={measure ? undefined : tab.name}
        aria-current={!measure && selected ? 'page' : undefined}
        className={classnames(
          selected
            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-500/15 dark:text-indigo-300'
            : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200',
          'relative flex cursor-pointer items-center rounded-md py-2 text-sm font-medium transition-[padding,gap] duration-200 ease-in-out',
          isTopLayout ? 'border-b-2' : 'border-l-2',
          showLabels ? 'justify-start gap-2 px-3' : 'justify-center px-1.5'
        )}
      >
        {Icon != null ? (
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[10px] font-semibold" aria-hidden>
            {tab.name.charAt(0)}
          </span>
        )}
        <span
          className={classnames(
            'min-w-0 truncate transition-[opacity,max-width] duration-150 ease-in-out',
            showLabels ? 'max-w-[12rem] flex-1 opacity-100' : 'max-w-0 opacity-0'
          )}
          aria-hidden={!showLabels}
        >
          {tab.name}
        </span>
        {hasTabBadge(tab.badge) && (
          <>
            <span
              className={classnames(
                'inline-flex items-center justify-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 transition-opacity duration-150 ease-in-out dark:bg-indigo-500/30 dark:text-indigo-200',
                showLabels ? 'opacity-100' : 'pointer-events-none absolute opacity-0'
              )}
            >
              {tab.badge}
            </span>
            <span
              className={classnames(
                'absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-indigo-500 transition-opacity duration-150 ease-in-out',
                showLabels ? 'pointer-events-none opacity-0' : 'opacity-100'
              )}
              aria-hidden
            />
          </>
        )}
      </a>
    )
  }

  const nav = (
        <nav
          className={classnames(
            'flex flex-col bg-white dark:bg-slate-800',
            // When portaled into the sticky account header, the shell already pins us.
            // Otherwise stick below that header; isolate so tab content cannot paint through.
            !navInStickyHeader && 'sticky isolate z-[19]',
            isTopLayout
              ? 'w-full shrink-0 overflow-hidden border-b border-slate-200 pb-2 dark:border-slate-700'
              : classnames(
                  'min-h-0 shrink-0 self-start overflow-hidden border-r border-slate-200 transition-[width,padding] duration-200 ease-in-out dark:border-slate-700',
                  showLabels ? 'w-52 pr-4' : 'w-10 pr-1.5'
                )
          )}
          style={
            navInStickyHeader
              ? undefined
              : {
                  top: 'var(--account-tab-sticky-top, 0px)',
                  ...(!isTopLayout
                    ? { maxHeight: 'calc(100dvh - var(--account-tab-sticky-top, 0px) - 0.75rem)' }
                    : {}),
                }
          }
          aria-label="Tabs"
        >
          {isTopLayout ? null : (
            <div className="mb-1 flex w-full shrink-0">
              <TabNavSettings
                navCollapsed={navCollapsed}
                isTopLayout={isTopLayout}
                onToggleCollapsed={toggleNavCollapsed}
                onToggleLayout={toggleNavLayout}
              />
            </div>
          )}
          <div
            ref={tabRowRef}
            className={classnames(
              isTopLayout
                ? 'flex min-w-0 flex-nowrap items-center gap-0.5 overflow-hidden'
                : 'flex min-h-0 flex-1 flex-col space-y-0.5 overflow-y-auto overflow-x-hidden'
            )}
          >
            {isTopLayout ? (
              <TabNavSettings
                navCollapsed={navCollapsed}
                isTopLayout={isTopLayout}
                onToggleCollapsed={toggleNavCollapsed}
                onToggleLayout={toggleNavLayout}
              />
            ) : null}
            {tabs.slice(0, overflowStart).map((tab, index) => (
              <RightTooltip
                key={tab.name}
                content={tab.name}
                enabled={!showLabels}
                placement={tooltipPlacement}
                triggerClassName={isTopLayout ? 'w-fit' : 'w-full'}
              >
                {renderTabTrigger(tab, index)}
              </RightTooltip>
            ))}
            {overflowItems.length > 0 ? (
              <TabMoreMenu
                items={overflowItems}
                showLabels={showLabels}
                hasSelected={overflowHasSelected}
                onSelect={selectTab}
              />
            ) : null}
          </div>
          {isTopLayout ? (
            <div
              ref={measureRowRef}
              className="pointer-events-none invisible absolute flex h-0 overflow-hidden"
              aria-hidden
            >
              <div className="flex flex-row flex-nowrap items-center gap-0.5">
                {tabs.map((tab, index) => renderTabTrigger(tab, index, true))}
                <div
                  ref={moreMeasureRef}
                  className={classnames(
                    'flex items-center rounded-md py-2 text-sm font-medium',
                    showLabels ? 'gap-2 px-3' : 'px-1.5'
                  )}
                >
                  <EllipsisHorizontalIcon className="h-4 w-4 shrink-0" />
                  {showLabels ? <span>More</span> : null}
                </div>
              </div>
            </div>
          ) : null}
        </nav>
  )

  const content = (
        <div className="relative z-0 min-w-0 flex-1 overflow-hidden px-1 pt-1" ref={contentRef}>
          <TabHeaderActionsContext.Provider value={setHeaderActions}>
            {(() => {
              const selectedTab = tabs[selectedKey]
              if (selectedTab == null) return null
              const SelectedIcon = selectedTab.Icon
              return (
                <>
                  <div className="mb-4 flex min-h-10 items-center gap-2 border-b border-slate-200 px-1 pt-1 pb-3 dark:border-slate-700">
                    {SelectedIcon != null && (
                      <SelectedIcon className="h-5 w-5 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="m-0 text-base font-semibold leading-none text-slate-900 dark:text-slate-100">
                          {selectedTab.name}
                        </h2>
                        {hasTabBadge(selectedTab.badge) && (
                          <span className="inline-flex items-center justify-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium leading-none text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-200">
                            {selectedTab.badge}
                          </span>
                        )}
                      </div>
                      {selectedTab.description != null && selectedTab.description !== '' && (
                        <p className="mt-1.5 mb-0 text-sm leading-snug text-slate-500 dark:text-slate-400">
                          {selectedTab.description}
                        </p>
                      )}
                    </div>
                    {headerActions != null && <div className="flex shrink-0 items-center gap-2">{headerActions}</div>}
                  </div>
                  {tabChildren[selectedKey]}
                </>
              )
            })()}
          </TabHeaderActionsContext.Provider>
        </div>
  )

  if (navSlots != null) {
    return (
      <div ref={rootRef} className="min-w-0">
        {navPortalHost != null ? createPortal(nav, navPortalHost) : null}
        {content}
      </div>
    )
  }

  return (
    <div ref={rootRef} className="min-w-0">
      <div className={classnames('min-w-0', isTopLayout ? 'flex flex-col gap-3' : 'flex gap-3 lg:gap-6')}>
        {nav}
        {content}
      </div>
    </div>
  )
}

export default TabPanel
